/**
 * Contenu principal du widget météo (status = ok) — orchestre header, strip 7j,
 * panneau heure explorée, graphique et footer onglets.
 *
 * Le wrapper d'animation collapse utilise `grid-template-rows: 0fr → 1fr` qui
 * anime de 0 à auto sans hauteur explicite (Chrome 117+ / FF 119+ / Safari 17.4+,
 * largement déployé en 2026). Fallback ancien browser : pas d'anim, le contenu
 * apparaît/disparaît instantanément (acceptable).
 *
 * Le `tickMaintenant` force le recalcul périodique de `indexMaintenant` sans
 * déclencher de refetch tRPC. Sans ça, un onglet resté ouvert plusieurs heures
 * verrait le marker "maintenant" pointer toujours sur l'heure d'il y a 2-3h.
 */

import { useEffect, useMemo, useState } from "react";

import { TICK_MAINTENANT_MS } from "./constants.js";
import { agregerParJour, getWeatherMeta, trouverIndexMaintenant } from "./formatters.js";
import { MeteoGraphique } from "./MeteoChart.js";
import { StripJours } from "./MeteoDayStrip.js";
import { MeteoEtatVide } from "./MeteoError.js";
import { MeteoHeaderLive } from "./MeteoHeader.js";
import { FooterOnglets } from "./MeteoLegend.js";
import { PanneauHeureExploree } from "./MeteoPanneauHeure.js";
import type { MeteoPoint, VariableMeteo } from "./types.js";

export function MeteoContenu({
  hourly,
  fetchedAt,
  commune,
  replie,
  onToggleReplie,
}: {
  hourly: MeteoPoint[];
  fetchedAt: string;
  commune: string;
  replie: boolean;
  onToggleReplie: () => void;
}) {
  const [tickMaintenant, setTickMaintenant] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTickMaintenant((t) => t + 1), TICK_MAINTENANT_MS);
    return () => clearInterval(interval);
  }, []);

  const indexMaintenant = useMemo(
    () => trouverIndexMaintenant(hourly),
    // `tickMaintenant` dépendance volontaire : on veut recalculer même si
    // `hourly` est stable, parce que "maintenant" évolue avec le temps réel.
    [hourly, tickMaintenant],
  );
  const pointMaintenant = hourly[indexMaintenant] ?? hourly[0];

  // Variable affichée dans le graphique (onglets bas).
  const [variableActive, setVariableActive] = useState<VariableMeteo>("temperature");

  // Curseur d'exploration : index dans `hourly` de l'heure que l'utilisateur
  // explore. Init = indexMaintenant ("voici maintenant").
  const [indexCurseur, setIndexCurseur] = useState<number>(indexMaintenant);
  // Re-synchronise si hourly change (refetch tRPC) — évite de pointer sur un
  // index obsolète après un refresh.
  useEffect(() => {
    setIndexCurseur(indexMaintenant);
  }, [indexMaintenant]);

  // Agrégation 7 jours (memo : recalcul uniquement si hourly change).
  const parJour = useMemo(() => agregerParJour(hourly, indexMaintenant), [hourly, indexMaintenant]);

  if (!pointMaintenant) {
    return <MeteoEtatVide message="Aucune donnée météo disponible." />;
  }

  const meta = getWeatherMeta(pointMaintenant.weather_code);
  const pointCurseur = hourly[indexCurseur] ?? pointMaintenant;
  const exploreUneAutreHeure = indexCurseur !== indexMaintenant;

  // ID stable du bloc contenu (pour aria-controls du bouton header). Un seul
  // widget météo par dashboard → ID en dur acceptable, plus simple que useId().
  const contentId = "meteo-widget-content";

  return (
    <div className="space-y-3">
      <MeteoHeaderLive
        point={pointMaintenant}
        meta={meta}
        commune={commune}
        replie={replie}
        onToggleReplie={onToggleReplie}
        controlsId={contentId}
      />

      <div
        id={contentId}
        className="grid"
        style={{
          gridTemplateRows: replie ? "0fr" : "1fr",
          transition: "grid-template-rows 320ms cubic-bezier(0.22, 0.61, 0.36, 1)",
        }}
        aria-hidden={replie}
      >
        <div style={{ overflow: "hidden" }}>
          <div className="space-y-3 pt-1">
            <StripJours
              parJour={parJour}
              indexCurseur={indexCurseur}
              indexMaintenant={indexMaintenant}
              hourly={hourly}
              onClickJour={setIndexCurseur}
            />
            <PanneauHeureExploree
              point={pointCurseur}
              estMaintenant={!exploreUneAutreHeure}
              onResetMaintenant={() => setIndexCurseur(indexMaintenant)}
            />
            <MeteoGraphique
              hourly={hourly}
              indexMaintenant={indexMaintenant}
              indexCurseur={indexCurseur}
              onChangeCurseur={setIndexCurseur}
              variableActive={variableActive}
            />
            <FooterOnglets
              variableActive={variableActive}
              onChange={setVariableActive}
              fetchedAt={fetchedAt}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
